import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = ({ currentLanguage, translations }) => {
  const [formData, setFormData] = useState({
    inquiryType: '',
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    position: '',
    phone: '',
    fundingStage: '',
    investmentAmount: '',
    industry: '',
    message: '',
    businessPlan: null,
    newsletter: false,
    gdprConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const inquiryTypes = [
    { value: 'partnership', label: translations?.form?.inquiryTypes?.partnership },
    { value: 'investment', label: translations?.form?.inquiryTypes?.investment },
    { value: 'general', label: translations?.form?.inquiryTypes?.general },
    { value: 'media', label: translations?.form?.inquiryTypes?.media }
  ];

  const fundingStages = [
    { value: 'pre-seed', label: translations?.form?.fundingStages?.preSeed },
    { value: 'seed', label: translations?.form?.fundingStages?.seed },
    { value: 'series-a', label: translations?.form?.fundingStages?.seriesA },
    { value: 'series-b', label: translations?.form?.fundingStages?.seriesB },
    { value: 'growth', label: translations?.form?.fundingStages?.growth }
  ];

  const industries = [
    { value: 'fintech', label: 'FinTech' },
    { value: 'ecommerce', label: 'E-Commerce' },
    { value: 'saas', label: 'SaaS' },
    { value: 'blockchain', label: 'Blockchain/Web3' },
    { value: 'ai-ml', label: 'AI/Machine Learning' },
    { value: 'healthtech', label: 'HealthTech' },
    { value: 'proptech', label: 'PropTech' },
    { value: 'other', label: translations?.form?.industries?.other }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file && file?.size <= 10 * 1024 * 1024) { // 10MB limit
      setFormData(prev => ({
        ...prev,
        businessPlan: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Mock submission delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setShowSuccess(true);

    // Reset form after success
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        inquiryType: '',
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        position: '',
        phone: '',
        fundingStage: '',
        investmentAmount: '',
        industry: '',
        message: '',
        businessPlan: null,
        newsletter: false,
        gdprConsent: false
      });
    }, 3000);
  };

  if (showSuccess) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">{translations?.form?.success?.title}</h3>
        <p className="text-gray-600 mb-6">{translations?.form?.success?.message}</p>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>{translations?.form?.success?.nextSteps?.title}</strong><br />
            {translations?.form?.success?.nextSteps?.description}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
        <h2 className="text-2xl font-bold text-white">{translations?.form?.title}</h2>
        <p className="text-blue-100 mt-2">{translations?.form?.subtitle}</p>
      </div>
      <form onSubmit={handleSubmit} className="p-8 space-y-6">
        {/* Inquiry Type */}
        <Select
          label={translations?.form?.fields?.inquiryType?.label}
          description={translations?.form?.fields?.inquiryType?.description}
          required
          options={inquiryTypes}
          value={formData?.inquiryType}
          onChange={(value) => handleInputChange('inquiryType', value)}
          placeholder={translations?.form?.fields?.inquiryType?.placeholder}
        />

        {/* Personal Information */}
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label={translations?.form?.fields?.firstName?.label}
            type="text"
            required
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            placeholder={translations?.form?.fields?.firstName?.placeholder}
          />
          <Input
            label={translations?.form?.fields?.lastName?.label}
            type="text"
            required
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            placeholder={translations?.form?.fields?.lastName?.placeholder}
          />
        </div>

        <Input
          label={translations?.form?.fields?.email?.label}
          type="email"
          required
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          placeholder={translations?.form?.fields?.email?.placeholder}
        />

        {/* Company Information */}
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label={translations?.form?.fields?.company?.label}
            type="text"
            required
            value={formData?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            placeholder={translations?.form?.fields?.company?.placeholder}
          />
          <Input
            label={translations?.form?.fields?.position?.label}
            type="text"
            value={formData?.position}
            onChange={(e) => handleInputChange('position', e?.target?.value)}
            placeholder={translations?.form?.fields?.position?.placeholder}
          />
        </div>

        <Input
          label={translations?.form?.fields?.phone?.label}
          type="tel"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          placeholder={translations?.form?.fields?.phone?.placeholder}
        />

        {/* Investment-specific fields */}
        {formData?.inquiryType === 'investment' && (
          <div className="space-y-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900">{translations?.form?.investmentSection?.title}</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Select
                label={translations?.form?.fields?.fundingStage?.label}
                options={fundingStages}
                value={formData?.fundingStage}
                onChange={(value) => handleInputChange('fundingStage', value)}
                placeholder={translations?.form?.fields?.fundingStage?.placeholder}
              />
              <Select
                label={translations?.form?.fields?.industry?.label}
                options={industries}
                value={formData?.industry}
                onChange={(value) => handleInputChange('industry', value)}
                placeholder={translations?.form?.fields?.industry?.placeholder}
              />
            </div>

            <Input
              label={translations?.form?.fields?.investmentAmount?.label}
              type="text"
              value={formData?.investmentAmount}
              onChange={(e) => handleInputChange('investmentAmount', e?.target?.value)}
              placeholder={translations?.form?.fields?.investmentAmount?.placeholder}
              description={translations?.form?.fields?.investmentAmount?.description}
            />

            {/* Business Plan Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations?.form?.fields?.businessPlan?.label}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="businessPlan"
                />
                <label htmlFor="businessPlan" className="cursor-pointer">
                  <Icon name="Upload" size={32} className="text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {formData?.businessPlan ? formData?.businessPlan?.name : translations?.form?.fields?.businessPlan?.placeholder}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{translations?.form?.fields?.businessPlan?.description}</p>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.form?.fields?.message?.label} *
          </label>
          <textarea
            required
            rows={6}
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            placeholder={translations?.form?.fields?.message?.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Checkboxes */}
        <div className="space-y-4">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData?.newsletter}
              onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{translations?.form?.fields?.newsletter?.label}</span>
          </label>

          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              required
              checked={formData?.gdprConsent}
              onChange={(e) => handleInputChange('gdprConsent', e?.target?.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              {translations?.form?.fields?.gdprConsent?.label} *
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          disabled={!formData?.gdprConsent}
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 py-4"
        >
          {isSubmitting ? translations?.form?.submitting : translations?.form?.submit}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;